/* Pincode check on the product page — see snippets/nv-pincode.liquid.

   India Post's public directory (api.postalpincode.in) names the district
   and state for a pincode and says when one does not exist. Its answers are
   written into the page with textContent only, never as markup.

   The delivery line ("Delivery by 9 Nov – 11 Nov. Order within 4h 15m")
   counts from today's dispatch cutoff in India time, whatever the shopper's
   own clock says: before the cutoff the order ships today, after it the
   dates and the countdown move to tomorrow. */
const IST = 'Asia/Kolkata';

class NvPincode extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.nv-pin__form');
    this.input = this.querySelector('.nv-pin__input');
    this.button = this.querySelector('.nv-pin__btn');
    this.result = this.querySelector('.nv-pin__result');

    try {
      const saved = localStorage.getItem('nv-pincode');
      if (saved) this.input.value = saved;
    } catch (e) {}

    this.input.addEventListener('input', () => {
      this.input.value = this.input.value.replace(/\D/g, '').slice(0, 6);
    });
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.check();
    });
  }

  async check() {
    const pin = this.input.value.trim();
    if (!/^[1-9]\d{5}$/.test(pin)) {
      this.show('error', [['', 'Please enter a valid 6-digit pincode.']]);
      this.input.focus();
      return;
    }

    this.button.disabled = true;
    this.show('pending', [['', 'Checking…']]);

    let place = '';
    let unknown = false;
    try {
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), 6000);
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`, { signal: abort.signal });
      clearTimeout(timer);
      const entry = (await res.json())[0];
      if (entry && entry.Status === 'Success' && entry.PostOffice && entry.PostOffice.length) {
        const office = entry.PostOffice[0];
        place = [office.District, office.State].filter(Boolean).join(', ');
      } else if (entry && entry.Status === 'Error') {
        unknown = true;
      }
    } catch (e) {
      // Directory unreachable: a well-formed pincode still gets the plain answer.
    }
    this.button.disabled = false;

    if (unknown) {
      this.show('error', [['', `We couldn't find pincode ${pin}. Please check it and try again.`]]);
      return;
    }

    try {
      localStorage.setItem('nv-pincode', pin);
    } catch (e) {}

    /* The delivery line says it all; the place is named only when no
       estimate is set, so the shopper still gets an answer. */
    const estimate = this.estimate();
    const lines = estimate
      ? [['truck', estimate]]
      : [['tick', place ? `Delivery available to ${place}` : 'Delivery available across India']];
    if (this.dataset.cod === 'true') lines.push(['tick', 'Cash on delivery available']);
    this.show('ok', lines);
  }

  /* [['Delivery by '], ['9 Nov – 11 Nov', true], ['. Order within '], ['4h 15m', true]] */
  estimate() {
    const min = parseInt(this.dataset.minDays, 10) || 0;
    const max = parseInt(this.dataset.maxDays, 10) || 0;
    if (!max) return null;
    const cutoff = parseInt(this.dataset.cutoff, 10) || 0;

    // Minutes past midnight, and the calendar date, as they are in India now.
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', { timeZone: IST, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
        .formatToParts(new Date())
        .map((p) => [p.type, p.value])
    );
    const nowMin = Number(parts.hour) * 60 + Number(parts.minute);
    const today = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));

    let shipDay = today;
    let left = 0;
    if (cutoff) {
      left = cutoff * 60 - nowMin;
      if (left <= 0) {
        shipDay += 86400000;
        left += 24 * 60;
      }
    }

    const fmt = (days) =>
      new Date(shipDay + days * 86400000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    const dates = min && min < max ? `${fmt(min)} – ${fmt(max)}` : fmt(max);

    const out = [['Delivery by '], [dates, true]];
    if (cutoff) out.push(['. Order within '], [`${Math.floor(left / 60)}h ${left % 60}m`, true]);
    return out;
  }

  show(state, lines) {
    this.result.hidden = false;
    this.result.dataset.state = state;
    this.result.replaceChildren(
      ...lines.map(([icon, content]) => {
        const p = document.createElement('p');
        p.className = 'nv-pin__line';
        if (icon) {
          const i = document.createElement('span');
          i.className = `nv-pin__icon nv-pin__icon--${icon}`;
          i.setAttribute('aria-hidden', 'true');
          p.append(i);
        }
        const text = document.createElement('span');
        for (const [chunk, bold] of typeof content === 'string' ? [[content]] : content) {
          if (bold) {
            const b = document.createElement('strong');
            b.textContent = chunk;
            text.append(b);
          } else {
            text.append(document.createTextNode(chunk));
          }
        }
        p.append(text);
        return p;
      })
    );
  }
}

if (!customElements.get('nv-pincode')) customElements.define('nv-pincode', NvPincode);
